import React, { useContext, useEffect, useRef, useState } from 'react';
import { LangContext } from '../../contexts/langContext';
import { SocialContext } from '../../contexts/socialContext';
import { StateContext } from '../../contexts/stateContext';
import './MobGuide.css';


export function MobGuide({
  narrative,
  hasMobMenu = false,
  always = false,
}) {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const $ = context.$;

  const [activeLink, setActiveLink] = useState('');
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const scrollRef = useRef(null);
  const itemRefs = useRef({});
  const ignoreSpyRef = useRef(false);
  const ignoreTimerRef = useRef(null);

  const pages = (narrative?.pages || [])
    .filter((one) => $('title', one))
    .filter((one) => !one['utility'])
    .filter((one) => !one.beta || context.state.version === 'β');

  useEffect(() => {
    if (always) {
      setIsScrolledDown(false);
      return undefined;
    }

    const handleScroll = (e) => {
      let scrollTop, viewportHeight, contentHeight;

      const target = e && e.target;
      if (target && target !== document && target.nodeType === 1) {
        if (target.clientHeight < window.innerHeight * 0.5) return;
        scrollTop = target.scrollTop;
        viewportHeight = target.clientHeight;
        contentHeight = target.scrollHeight;
      } else {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        viewportHeight = window.innerHeight;
        contentHeight = document.documentElement.scrollHeight;
      }

      if (scrollTop > 120) {
        setIsScrolledDown(scrollTop > lastScrollTop);
        setLastScrollTop(scrollTop);
      }

      if (scrollTop + viewportHeight >= contentHeight - 10) {
        setIsScrolledDown(false);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [always, lastScrollTop]);

  useEffect(() => {
    const probeY = () => window.innerHeight * 0.5;

    const updateActive = () => {
      if (ignoreSpyRef.current) return;
      const y = probeY();
      let bestId = '';
      let bestDist = Infinity;

      document.querySelectorAll('.x_edu_content0').forEach((el) => {
        if (!el.id) return;
        const rect = el.getBoundingClientRect();
        if (rect.height <= 0) return;
        const dist = y < rect.top ? rect.top - y : y > rect.bottom ? y - rect.bottom : 0;
        if (dist < bestDist) {
          bestDist = dist;
          bestId = el.id;
        }
      });

      if (bestId) setActiveLink(bestId);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateActive();
        ticking = false;
      });
    };

    updateActive();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [narrative]);

  useEffect(() => {
    if (!activeLink) return;
    const container = scrollRef.current;
    const item = itemRefs.current[activeLink];
    if (!container || !item) return;

    const left = item.offsetLeft - container.clientWidth / 2 + item.clientWidth / 2;
    container.scrollTo({
      left: Math.max(0, left),
      behavior: 'smooth',
    });
  }, [activeLink]);

  useEffect(() => () => {
    if (ignoreTimerRef.current) clearTimeout(ignoreTimerRef.current);
  }, []);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (!section) return;

    ignoreSpyRef.current = true;
    setActiveLink(id);
    if (ignoreTimerRef.current) clearTimeout(ignoreTimerRef.current);

    section.scrollIntoView({ behavior: 'smooth' });

    const clearIgnore = () => {
      ignoreSpyRef.current = false;
      window.removeEventListener('scrollend', clearIgnore, true);
    };

    window.addEventListener('scrollend', clearIgnore, true);
    // Fallback for browsers without scrollend / nested scrollers
    ignoreTimerRef.current = setTimeout(() => {
      ignoreSpyRef.current = false;
      window.removeEventListener('scrollend', clearIgnore, true);
    }, 900);
  };

  if (!pages.length) return null;

  return (
    <div
      className={[
        'x_mob_guide0',
        always ? 'x_mob_guide0--always' : '',
        hasMobMenu ? 'x_mob_guide0--with-menu' : '',
        context.state.calcShown ? 'x_mob_guide0--calc' : '',
        !always && isScrolledDown ? 'x_mob_guide0--scrolled' : '',
      ].filter(Boolean).join(' ')}
      aria-label="Page sections"
    >
      <div className="x_mob_guide" ref={scrollRef}>
        {pages.map((one) => {
          const oneId = one.id;
          const isActive = activeLink === oneId;
          return (
            <button
              type="button"
              key={`mob_nav_${oneId}`}
              ref={(el) => {
                if (el) itemRefs.current[oneId] = el;
                else delete itemRefs.current[oneId];
              }}
              className={`x_mob_guide_item${isActive ? ' active' : ''}`}
              onClick={() => scrollToSection(oneId)}
            >
              <span className="x_mob_guide_label">{$('title', one)}</span>
              {isActive && <span className="x_mob_guide_active" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
